import { COLOR_MATCH, hueDistance } from '../constants';
import type { Camera } from '../engine/Camera';
import type { FoodField } from '../entities/Food';

/**
 * Food renderer with LOD + "your color calls" emphasis.
 *
 * Three passes:
 *   1. Dot pass — for pellets too small to bother gradients. fillRect.
 *   2. Standard pellet pass — radial-gradient core, single-hue, breathing.
 *   3. Match emphasis pass — for pellets matching the player's team hue,
 *      add an extra outer halo that pulls the eye toward "your" food.
 *
 * Visibility note: each pellet is single-hue (matches its team color) with
 * lightness varying inner→outer. Same color discipline as the snakes.
 */
export class FoodRenderer {
  draw(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    field: FoodField,
    time: number,
    playerHue: number,
  ): void {
    const bounds = camera.getVisibleBounds();
    const pellets = field.pellets;
    const z = camera.zoom;
    const cx = camera.viewportW * 0.5 + camera.shakeX;
    const cy = camera.viewportH * 0.5 + camera.shakeY;
    const cax = camera.position.x;
    const cay = camera.position.y;

    const visIdx: number[] = FoodRenderer.tmpIdx;
    visIdx.length = 0;
    for (let i = 0; i < pellets.length; i++) {
      const p = pellets[i]!;
      if (p.x + p.radius < bounds.minX || p.x - p.radius > bounds.maxX) continue;
      if (p.y + p.radius < bounds.minY || p.y - p.radius > bounds.maxY) continue;
      visIdx.push(i);
    }
    if (visIdx.length === 0) return;

    // ---- Match-emphasis pre-pass (drawn UNDER everything so it reads as aura) ----
    // For pellets matching the player's color: faint outer halo to draw the eye.
    ctx.save();
    ctx.shadowBlur = 22;
    for (let k = 0; k < visIdx.length; k++) {
      const p = pellets[visIdx[k]!]!;
      if (hueDistance(p.hue, playerHue) > COLOR_MATCH.PERFECT_DEGREES) continue;
      const r = p.radius * z;
      if (r < 2) continue;
      const sx = (p.x - cax) * z + cx;
      const sy = (p.y - cay) * z + cy;
      const haloR = r * 3.2;
      ctx.shadowColor = `hsl(${p.hue} 100% 65%)`;
      ctx.globalAlpha = 0.55;
      const g = ctx.createRadialGradient(sx, sy, r * 0.5, sx, sy, haloR);
      g.addColorStop(0, `hsla(${p.hue} 100% 65% / 0.45)`);
      g.addColorStop(1, `hsla(${p.hue} 100% 50% / 0)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(sx, sy, haloR, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    ctx.globalAlpha = 1;

    // ---- LOD dot pass ----
    ctx.save();
    for (let k = 0; k < visIdx.length; k++) {
      const p = pellets[visIdx[k]!]!;
      const r = p.radius * z;
      if (r >= 2.5) continue;
      const sx = (p.x - cax) * z + cx;
      const sy = (p.y - cay) * z + cy;
      ctx.fillStyle = `hsl(${p.hue} 95% 65%)`;
      ctx.fillRect(sx - 1, sy - 1, 2, 2);
    }
    ctx.restore();

    // ---- Full pellet pass ----
    ctx.save();
    ctx.shadowBlur = 14;
    for (let k = 0; k < visIdx.length; k++) {
      const p = pellets[visIdx[k]!]!;
      const r = p.radius * z;
      if (r < 2.5) continue;

      const sx = (p.x - cax) * z + cx;
      const sy = (p.y - cay) * z + cy;
      const pulse = 1 + 0.15 * Math.sin(time * 2.4 + p.phase);
      const rr = r * pulse;

      ctx.shadowColor = `hsl(${p.hue} 100% 65%)`;
      const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, rr);
      g.addColorStop(0, `hsl(${p.hue} 100% 88%)`);
      g.addColorStop(0.6, `hsl(${p.hue} 100% 58%)`);
      g.addColorStop(1, `hsla(${p.hue} 100% 38% / 0)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(sx, sy, rr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // ---- Sparkle accent (slow rotating bright dot per pellet) ----
    ctx.save();
    ctx.shadowBlur = 0;
    for (let k = 0; k < visIdx.length; k++) {
      const p = pellets[visIdx[k]!]!;
      const r = p.radius * z;
      if (r < 3) continue;
      const angle = time * 0.9 + p.phase;
      const orbit = r * 0.55;
      const sx = (p.x - cax) * z + cx + Math.cos(angle) * orbit;
      const sy = (p.y - cay) * z + cy + Math.sin(angle) * orbit;
      ctx.fillStyle = `hsla(${p.hue} 100% 95% / 0.75)`;
      ctx.beginPath();
      ctx.arc(sx, sy, Math.max(0.8, r * 0.18), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // ---- Ejected-pellet motion streak ----
    // Pellets with active velocity get a gradient tail in the direction
    // opposite their motion. Reads as "thrown projectile" not "static food".
    ctx.save();
    ctx.lineCap = 'round';
    for (let k = 0; k < visIdx.length; k++) {
      const p = pellets[visIdx[k]!]!;
      const speed2 = p.vx * p.vx + p.vy * p.vy;
      if (speed2 < 400) continue; // ignore essentially-still pellets
      const r = p.radius * z;
      const sx = (p.x - cax) * z + cx;
      const sy = (p.y - cay) * z + cy;
      const tailX = sx - p.vx * z * 0.04;
      const tailY = sy - p.vy * z * 0.04;
      const grad = ctx.createLinearGradient(sx, sy, tailX, tailY);
      grad.addColorStop(0, `hsla(${p.hue} 100% 80% / 0.7)`);
      grad.addColorStop(1, `hsla(${p.hue} 100% 60% / 0)`);
      ctx.strokeStyle = grad;
      ctx.lineWidth = Math.max(1.2, r * 0.9);
      ctx.shadowColor = `hsl(${p.hue} 100% 70%)`;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(tailX, tailY);
      ctx.stroke();
    }
    ctx.restore();
  }

  private static tmpIdx: number[] = [];
}
